import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.getMe();
      setUser(data.user);
      // Store workspace ID for API header
      if (data.user?.workspace?.id) {
        localStorage.setItem('activeWorkspaceId', data.user.workspace.id);
      }
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeWorkspaceId');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.user?.workspace?.id) {
      localStorage.setItem('activeWorkspaceId', data.user.workspace.id);
    }
    setUser(data.user);
    return data;
  };

  const register = async (formData) => {
    const { data } = await authAPI.register(formData);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeWorkspaceId');
    setUser(null);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const canEdit = ['super_admin', 'admin', 'analyst'].includes(user?.role);
  const canCreate = ['super_admin', 'admin', 'analyst', 'field_worker'].includes(user?.role);
  
  // Feature gating based on workspace plan
  const enabledFeatures = user?.workspace?.subscription?.enabledFeatures || [];
  const hasFeature = (featureKey) => {
    if (user?.role === 'super_admin') return true; // super admin sees everything
    if (!enabledFeatures || enabledFeatures.length === 0) return true; // no restrictions set = all features enabled
    return enabledFeatures.includes(featureKey);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loadUser, isAdmin, canEdit, canCreate, hasFeature, enabledFeatures }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};